import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { CardPaymentForecast } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { signPrefix } from '../lib/amount-format'
import { cardPaymentsApi } from '../lib/api'

/** "2026-08-29" → "8/29"。表の中では年を省いて読みやすくする */
function shortDate(date: string): string {
  const [, m, d] = date.split('-')
  return `${Number(m)}/${Number(d)}`
}

/**
 * クレジットカードの引き落とし予定と、引き落とし先口座の残高見込み。
 *
 * 「引き落とし日にお金が足りない」を事前に気付けるようにするのが目的なので、
 * ページ上部に置き、口座ごとに分けず 1 枚の詳細表に畳んで数行で収める。
 * 不足する行は赤で目立たせ、口座単位の警告を表の前に出す。
 * 現在残高やその他の入出金・注意書きは畳んだ詳細に逃がす。
 */
export function CardPaymentForecastPanel({
  token,
  /** 予測する月数。既定はサーバー側の 3 ヶ月 */
  months,
  /** 再取得のきっかけ。カードを編集したあとに値を変えると取り直す */
  refreshKey,
}: {
  token: string
  months?: number
  refreshKey?: unknown
}) {
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const [forecast, setForecast] = useState<CardPaymentForecast | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    setLoading(true)
    cardPaymentsApi.forecast(token, months ? { months } : undefined)
      .then((res) => { setForecast(res.data); setError(null) })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }, [token, months, refreshKey])

  if (loading && !forecast) {
    return <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</p>
  }
  if (error) {
    return (
      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
        {error}
      </div>
    )
  }
  if (!forecast) return null

  const shortfalls = forecast.accounts.flatMap((account) =>
    account.payments
      .filter((p) => p.shortfall > 0)
      .map((p) => ({ account, payment: p })),
  )

  // 口座ごとの表を並べると縦に伸びるので、1 枚の表に畳んで引き落とし日順に並べる。
  // 予測残高は口座ごとに積み上がった値なので、行に引き落とし先を添えて読めるようにする
  const rows = forecast.accounts
    .flatMap((account) => account.payments.map((payment) => ({ account, payment })))
    .sort((a, b) =>
      a.payment.paymentDate.localeCompare(b.payment.paymentDate) ||
      a.account.accountName.localeCompare(b.account.accountName),
    )

  const hasDetails =
    forecast.unconfiguredCards.length > 0 ||
    forecast.accounts.length > 0

  return (
    <div className="mt-4 rounded-xl border border-gray-100 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('cpf_title')}</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('cpf_subtitle').replace('{until}', forecast.until)}
        </p>
      </div>

      {/* 不足する引き落としを表より前にまとめて出す。表を読まなくても気付けるように */}
      {shortfalls.length > 0 && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {shortfalls.map(({ account, payment }) => (
            <p key={`${account.accountId}-${payment.cardId}-${payment.paymentDate}`} className="font-medium">
              &#9888;{' '}
              {t('cpf_shortfall_alert')
                .replace('{account}', account.accountName)
                .replace('{date}', payment.paymentDate)
                .replace('{amount}', formatJPY(payment.shortfall))}
            </p>
          ))}
        </div>
      )}

      {forecast.accounts.length === 0 ? (
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">{t('cpf_empty')}</p>
      ) : (
        <>
          {shortfalls.length === 0 && (
            <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">
              {t('cpf_ok').replace('{until}', forecast.until)}
            </p>
          )}
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 dark:text-gray-500">
                  <th className="py-1 pr-3 font-normal">{t('cpf_column_date')}</th>
                  <th className="py-1 pr-3 font-normal">{t('cpf_column_card')}</th>
                  <th className="py-1 pr-3 font-normal">{t('cpf_column_account')}</th>
                  <th className="py-1 pr-3 font-normal text-right">{t('cpf_column_amount')}</th>
                  <th className="py-1 font-normal text-right">{t('cpf_column_projected')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ account, payment: p }) => (
                  <tr
                    key={`${account.accountId}-${p.cardId}-${p.paymentDate}`}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-1.5 pr-3 whitespace-nowrap text-gray-900 dark:text-gray-100">
                      {shortDate(p.paymentDate)}
                      <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">
                        {t('cpf_closing').replace('{date}', shortDate(p.closingDate))}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        {p.cardColor && (
                          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: p.cardColor }} />
                        )}
                        {p.cardName}
                        {p.status === 'scheduled' && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">{t('cpf_status_scheduled')}</span>
                        )}
                      </span>
                    </td>
                    <td
                      className="py-1.5 pr-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400"
                      title={`${t('cpf_balance_now')}: ${formatJPY(account.balanceAsOfToday)}`}
                    >
                      {account.accountName}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums whitespace-nowrap text-gray-900 dark:text-gray-100">
                      {formatJPY(p.amount)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums whitespace-nowrap">
                      <span className={p.shortfall > 0 ? 'font-semibold text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}>
                        {formatJPY(p.projectedBalance)}
                      </span>
                      {p.shortfall > 0 && (
                        <span className="ml-1.5 inline-block rounded px-1 py-0.5 text-xs bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
                          {t('cpf_shortfall_badge').replace('{amount}', formatJPY(p.shortfall))}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 現在残高・織り込んだ入出金・未設定カード・注意書きは、
          普段は畳んでおいて必要なときだけ開く */}
      {hasDetails && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-gray-400 dark:text-gray-500">
            {t('cpf_details')}
          </summary>
          <div className="mt-2 space-y-2">
            {forecast.unconfiguredCards.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('cpf_unconfigured').replace('{names}', forecast.unconfiguredCards.map((c) => c.name).join('、'))}
              </p>
            )}
            {forecast.accounts.map((account) => (
              <div key={account.accountId} className="text-xs">
                <p className="text-gray-500 dark:text-gray-400">
                  {account.accountName}
                  {' — '}
                  {t('cpf_balance_now')}:{' '}
                  <span className="font-medium text-gray-700 tabular-nums dark:text-gray-300">
                    {formatJPY(account.balanceAsOfToday)}
                  </span>
                </p>
                {account.otherEvents.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {account.otherEvents.map((e, i) => (
                      <li key={`${e.date}-${e.description}-${i}`} className="flex justify-between gap-3 text-gray-500 dark:text-gray-400">
                        <span>
                          {shortDate(e.date)} {e.description}
                          <span className="ml-1.5 text-gray-400 dark:text-gray-500">
                            {e.source === 'recurring' ? t('cpf_source_recurring') : t('cpf_source_scheduled')}
                          </span>
                        </span>
                        <span className={`tabular-nums ${e.amount >= 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                          {signPrefix(e.amount)}{formatJPY(Math.abs(e.amount))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <p className="text-xs text-gray-400 dark:text-gray-500">{t('cpf_note')}</p>
          </div>
        </details>
      )}
    </div>
  )
}

/**
 * 引き落とし残高が足りない見込みのときだけ出る警告バナー。
 * ダッシュボードに置き、詳細はクレジットカードページに任せる。
 */
export function CardPaymentShortfallBanner({ token }: { token: string }) {
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const [forecast, setForecast] = useState<CardPaymentForecast | null>(null)

  useEffect(() => {
    if (!token) return
    // ダッシュボードの主役ではないので、取得に失敗しても黙って出さない
    void cardPaymentsApi.forecast(token)
      .then((res) => setForecast(res.data))
      .catch(() => undefined)
  }, [token])

  const shortfalls = (forecast?.accounts ?? []).flatMap((account) =>
    account.payments.filter((p) => p.shortfall > 0).map((p) => ({ account, payment: p })),
  )
  if (shortfalls.length === 0) return null

  return (
    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 dark:bg-red-950 dark:border-red-800 dark:text-red-300">
      {shortfalls.map(({ account, payment }) => (
        <p key={`${account.accountId}-${payment.cardId}-${payment.paymentDate}`} className="font-medium">
          &#9888;{' '}
          {t('cpf_shortfall_alert')
            .replace('{account}', account.accountName)
            .replace('{date}', payment.paymentDate)
            .replace('{amount}', formatJPY(payment.shortfall))}
        </p>
      ))}
      <Link to="/credit-cards" className="mt-1 inline-block text-xs underline">
        {t('cpf_banner_link')}
      </Link>
    </div>
  )
}
